import commaNumber from 'comma-number';
import {
  Archive,
  ArrowUpFromDot,
  Asterisk,
  AtSign,
  BadgeDollarSign,
  Blocks,
  BookUser,
  Bot,
  Calendar,
  ChevronsUp,
  CircleQuestionMark,
  Flame,
  Globe,
  Hash,
  Headset,
  Heart,
  Link,
  PackageSearch,
  Percent,
  PhoneOutgoing,
  ScrollText,
  Sigma,
  TicketCheck,
  UserIcon,
  Workflow,
  Zap,
} from 'lucide-react';
import { type ReactNode, useCallback } from 'react';

import { Flex } from '@/components/base';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useLocaleStore from '@/stores/LocaleStore';
import { adPredictionList } from '@/types/feedbackConst';
import feedbackUtils from '@/utils/feedback';

import { FEEDBACK_FIELD } from '../configs';
import SourcePropertyValue from '../FeedbackDetail/ChannelSourcePropertyValue';
import { AgentField } from '../FeedbackDetail/PropertyArea/Fields/AgentField';
import { BookmarkField } from '../FeedbackDetail/PropertyArea/Fields/BookmarkField';
import { CategoryField } from '../FeedbackDetail/PropertyArea/Fields/CategoryField';
import { CustomerField } from '../FeedbackDetail/PropertyArea/Fields/CustomerField';
import { DataOrioginField } from '../FeedbackDetail/PropertyArea/Fields/DataOrioginField';
import { DateField } from '../FeedbackDetail/PropertyArea/Fields/DateField';
import { DemographicField } from '../FeedbackDetail/PropertyArea/Fields/DemographicField';
import { EndSentimentField } from '../FeedbackDetail/PropertyArea/Fields/EndSentimentField';
import { EngagementField } from '../FeedbackDetail/PropertyArea/Fields/EngagementField';
import { KeySentimentField } from '../FeedbackDetail/PropertyArea/Fields/KeySentimentField';
import { PriorityField } from '../FeedbackDetail/PropertyArea/Fields/PriorityField';
import { ProductField } from '../FeedbackDetail/PropertyArea/Fields/ProductField';
import { PropertyRowWrapper } from '../FeedbackDetail/PropertyArea/Fields/PropertyRowWrapper';
import { RatingScoreField } from '../FeedbackDetail/PropertyArea/Fields/RatingScoreField';
import { ScoreField } from '../FeedbackDetail/PropertyArea/Fields/ScoreField';
import { SentimentField } from '../FeedbackDetail/PropertyArea/Fields/SentimentField';
import { StringField } from '../FeedbackDetail/PropertyArea/Fields/StringField';
import { SurveyField } from '../FeedbackDetail/PropertyArea/Fields/SurveyField';
import { TagField } from '../FeedbackDetail/PropertyArea/Fields/TagField';
import { TopicField } from '../FeedbackDetail/PropertyArea/Fields/TopicField';
import { ValueWrapper } from '../FeedbackDetail/PropertyArea/ValueWrapper';

export const useFeedbackDetailProperty = ({
  isStatic,
  feedback,
  showFull,
  setShowFull,
}: {
  isStatic: boolean;
  feedback: FeedbackT;
  showFull: boolean;
  setShowFull: (showFull: boolean) => void;
}) => {
  const languages = useLocaleStore((state) => state.languageList);
  const getFieldComponent = useCallback(
    (field_key: FEEDBACK_FIELD): ReactNode => {
      switch (field_key) {
        case FEEDBACK_FIELD.DATE:
          return (
            <DateField
              key={field_key}
              icon={<Calendar />}
              label="Date"
              feedbackAlias={feedback.alias}
              date={feedback.date}
              showFull={showFull}
              isStatic={isStatic}
            />
          );
        case FEEDBACK_FIELD.CUSTOMER_INFO:
          return (
            <CustomerField
              key={field_key}
              icon={<UserIcon />}
              label="Customer"
              showFull={showFull}
              isStatic={isStatic}
              compnayName={feedback.company_info?.name}
              customerName={feedback.customer_info?.name}
              customerEmail={feedback.customer_info?.email}
            />
          );
        case FEEDBACK_FIELD.MANAGER:
          return (
            <AgentField
              key={field_key}
              icon={<Headset />}
              label="Agent"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              agent_alias={feedback.manager}
            />
          );
        case FEEDBACK_FIELD.SOURCE: {
          return (
            <PropertyRowWrapper
              key={field_key}
              icon={<Hash />}
              label="Source"
              showFull={showFull}
              isStatic={isStatic}
            >
              <ValueWrapper>
                <SourcePropertyValue {...feedback.source} />
              </ValueWrapper>
            </PropertyRowWrapper>
          );
        }
        case FEEDBACK_FIELD.SOURCE_ID: {
          return (
            <StringField
              key={field_key}
              icon={<Workflow />}
              label="Source ID"
              showFull={showFull}
              isStatic={isStatic}
              value={feedback.source.target}
              useCopy
            />
          );
        }
        case FEEDBACK_FIELD.BOOKMARKS:
          return (
            <BookmarkField
              key={field_key}
              icon={<Link />}
              label="Related links"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              bookmarks={feedback.bookmarks || []}
            />
          );
        case FEEDBACK_FIELD.DATA_ORIGIN:
          return (
            <DataOrioginField
              key={field_key}
              icon={<ArrowUpFromDot />}
              label="Data origin"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              data_origin={feedback.data_origin || null}
            />
          );
        case FEEDBACK_FIELD.LABELS:
          return (
            <TagField
              key={field_key}
              icon={<Hash />}
              label="Tag"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              labels={feedback.labels}
              setShowFull={setShowFull}
            />
          );
        case FEEDBACK_FIELD.STATUS:
          return (
            <StringField
              key={field_key}
              icon={<Bot />}
              label="Analysis scope"
              showFull={showFull}
              isStatic={isStatic}
              value={feedbackUtils.getFeedbackStatusLabel(feedback.status)}
              tooltip={
                <Flex>
                  Analysis scope indicates
                  <br />
                  the scope of AI analysis.
                  <br />
                  It will be changed after ticket is closed.
                </Flex>
              }
            />
          );
        case FEEDBACK_FIELD.PRIORITY:
          return (
            <PriorityField
              key={field_key}
              icon={<ChevronsUp />}
              label="Priority"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              priority={feedback.priority}
            />
          );
        case FEEDBACK_FIELD.RATING_SCORE:
          return (
            <RatingScoreField
              key={field_key}
              icon={<Hash />}
              label="Rating score"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              rating_score={feedback.rating_score}
            />
          );
        case FEEDBACK_FIELD.SCORES:
          return (
            <ScoreField
              key={field_key}
              icon={<Hash />}
              label="Score"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              rating_score={feedback.rating_score}
              nps_score={feedback.nps_score}
              csat_score={feedback.csat_score}
              ces_score={feedback.ces_score}
            />
          );

        case FEEDBACK_FIELD.DIRECTION:
          return (
            <StringField
              key={field_key}
              icon={<PhoneOutgoing />}
              label="Direction"
              showFull={showFull}
              isStatic={isStatic}
              value={feedback.direction}
            />
          );
        case FEEDBACK_FIELD.TOPIC2_LIST:
          return (
            <TopicField
              key={field_key}
              icon={<Blocks />}
              label="Taxonomy"
              ai_generated={true}
              showFull={showFull}
              isStatic={isStatic}
              topic2_list={feedback.topic2_list}
              setShowFull={setShowFull}
            />
          );
        case FEEDBACK_FIELD.SENTIMENT:
          return (
            <SentimentField
              key={field_key}
              icon={<Heart />}
              label="Sentiment"
              ai_generated={true}
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              sentiment_detail={feedback.blocks?.map(
                (block) => block.sentiment,
              )}
            />
          );
        case FEEDBACK_FIELD.ENDING_SENTIMENT:
          return (
            <EndSentimentField
              key={field_key}
              icon={<Heart />}
              label="Ending sentiment"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              end_sentiment={feedback.ending_sentiment}
              ai_generated={true}
            />
          );
        case FEEDBACK_FIELD.KEY_SENTIMENT:
          return (
            <KeySentimentField
              key={field_key}
              icon={<Heart />}
              label="Key sentiment"
              showFull={showFull}
              isStatic={isStatic}
              feedback_alias={feedback.alias}
              key_sentiment={feedback.sentiment}
              ai_generated={true}
            />
          );

        case FEEDBACK_FIELD.CLASSIFICATION:
          return (
            <CategoryField
              key={field_key}
              icon={<Archive />}
              label="Category"
              ai_generated={true}
              showFull={showFull}
              isStatic={isStatic}
              classification={feedback.classification || []}
            />
          );
        case FEEDBACK_FIELD.REMARKS:
          return (
            <StringField
              key={field_key}
              icon={<Asterisk />}
              label="Remarks"
              showFull={showFull}
              isStatic={isStatic}
              value={feedback.remarks?.join(', ')}
              expandable={true}
            />
          );
        case FEEDBACK_FIELD.LANGUAGE:
          return (
            <StringField
              key={field_key}
              icon={<Globe />}
              label="Language"
              showFull={showFull}
              isStatic={isStatic}
              value={
                languages.find(
                  (language) => language.code === feedback.language,
                )?.name
              }
            />
          );
        case FEEDBACK_FIELD.SOURCE_STATUS: {
          const sourceStatus = feedbackUtils.getSourceStatusLabel(
            feedback?.source_status,
          );

          return (
            <StringField
              key={field_key}
              icon={<TicketCheck />}
              label="Ticket status"
              showFull={showFull}
              isStatic={isStatic}
              value={sourceStatus?.value}
              valueIcon={sourceStatus?.icon}
              placeholder="Not applicable"
              tooltip={
                <Flex>
                  Ticket status indicates
                  <br />
                  the current status of each ticket
                </Flex>
              }
            />
          );
        }

        case FEEDBACK_FIELD.PRODUCTS: {
          return (
            <ProductField
              key={field_key}
              icon={<PackageSearch />}
              label="Product info"
              showFull={showFull}
              isStatic={isStatic}
              products={feedback.products || []}
            />
          );
        }
        case FEEDBACK_FIELD.DEMOGRAPHIC: {
          return (
            <DemographicField
              key={field_key}
              icon={<BookUser />}
              label="Demographic"
              showFull={showFull}
              isStatic={isStatic}
              demographic={feedback.demographic}
            />
          );
        }
        case FEEDBACK_FIELD.SURVEY: {
          return (
            <SurveyField
              key={field_key}
              icon={<ScrollText />}
              label="Survey"
              showFull={showFull}
              isStatic={isStatic}
              value={feedback.survey?.id}
            />
          );
        }
        case FEEDBACK_FIELD.ENGAGEMENT: {
          return (
            <EngagementField
              key={field_key}
              icon={<Flame />}
              label="Engagement"
              showFull={showFull}
              isStatic={isStatic}
              engagement={feedback.engagement}
            />
          );
        }
        case FEEDBACK_FIELD.HASHTAGS: {
          const hashtags = feedback.hashtags;
          return (
            <PropertyRowWrapper
              key={field_key}
              icon={<Hash />}
              label="Hashtags"
              showFull={showFull}
              isStatic={isStatic}
              expandable
            >
              <div className="flex flex-wrap gap-2 gap-y-1">
                {hashtags && hashtags.length > 0 ? (
                  hashtags.map((hashtag) => (
                    <ValueWrapper key={hashtag}>
                      <Hash className="text-gray-600 size-3" />
                      {hashtag}
                    </ValueWrapper>
                  ))
                ) : (
                  <ValueWrapper className="text-gray-500">
                    {NOT_ASSIGNED_LABEL}
                  </ValueWrapper>
                )}
              </div>
            </PropertyRowWrapper>
          );
        }
        case FEEDBACK_FIELD.MENTIONS: {
          return (
            <PropertyRowWrapper
              key={field_key}
              icon={<Hash />}
              label="Mentions"
              showFull={showFull}
              isStatic={isStatic}
              expandable
            >
              <div className="flex flex-wrap gap-2 gap-y-1">
                {feedback.mentions && feedback.mentions.length > 0 ? (
                  feedback.mentions.map((mention) => (
                    <ValueWrapper key={mention}>
                      <AtSign className="text-gray-600 size-3" />
                      {mention}
                    </ValueWrapper>
                  ))
                ) : (
                  <ValueWrapper className="text-gray-500">
                    {NOT_ASSIGNED_LABEL}
                  </ValueWrapper>
                )}
              </div>
            </PropertyRowWrapper>
          );
        }
        case FEEDBACK_FIELD.AD_PREDICTION: {
          return (
            <PropertyRowWrapper
              key={field_key}
              ai_generated
              icon={<BadgeDollarSign />}
              label="Ad prediction"
              showFull={showFull}
              isStatic={isStatic}
            >
              {feedback.ad_prediction ? (
                <TooltipV2
                  tooltipNode={<div>{feedback.ad_prediction_reason}</div>}
                >
                  <ValueWrapper>
                    {adPredictionList.get(feedback.ad_prediction)}

                    <CircleQuestionMark className="text-gray-600 size-3" />
                  </ValueWrapper>
                </TooltipV2>
              ) : (
                <ValueWrapper className="text-gray-500">
                  {NOT_ASSIGNED_LABEL}
                </ValueWrapper>
              )}
            </PropertyRowWrapper>
          );
        }
        case FEEDBACK_FIELD.IMPRESSION: {
          return (
            <StringField
              isStatic={isStatic}
              showFull={showFull}
              key={field_key}
              icon={<Zap />}
              label="Impression"
              placeholder="-"
              value={feedback.impression && commaNumber(feedback.impression)}
              tooltip={
                <>
                  The total number of times your content was displayed.
                  <br />
                  It is measured by Views for video-centric platforms
                  <br />
                  (X, TikTok, Reels, YouTube)
                  <br />
                  and by a sum of primary interactions or upvotes for others.
                </>
              }
            />
          );
        }
        case FEEDBACK_FIELD.ENGAGEMENT_RATE: {
          return (
            <StringField
              isStatic={isStatic}
              showFull={showFull}
              key={field_key}
              icon={<Percent />}
              label="Engagement rate"
              suffix=" %"
              placeholder="-"
              value={
                feedback.engagement?.rate &&
                commaNumber(feedback.engagement.rate.toFixed(1))
              }
              tooltip={
                <>
                  The percentage of viewers who interacted with your content.
                  <br />
                  Calculated as (Total Engagement ÷ Impression) × 100.
                  <br />
                  This metric is available for platforms where Impression are
                  measured by Views.
                </>
              }
            />
          );
        }
        case FEEDBACK_FIELD.TOTAL_ENGAGEMENT: {
          return (
            <StringField
              isStatic={isStatic}
              showFull={showFull}
              key={field_key}
              icon={<Sigma />}
              label="Total engagement"
              placeholder="-"
              tooltip={
                <>
                  The total sum of interactions on your content.
                  <br />
                  This includes Likes and Comments across all platforms,
                  <br />
                  with the addition of Shares (or Reposts) for X and TikTok,
                  <br />
                  and Upvotes for Reddit.
                </>
              }
              value={
                feedback.engagement?.total &&
                commaNumber(feedback.engagement.total)
              }
            />
          );
        }
        default:
          throw new Error(`Invalid feedback field name: ${field_key}`);
      }
    },
    [
      feedback.ad_prediction,
      feedback.ad_prediction_reason,
      feedback.alias,
      feedback.blocks,
      feedback.bookmarks,
      feedback.ces_score,
      feedback.classification,
      feedback.company_info?.name,
      feedback.csat_score,
      feedback.customer_info?.email,
      feedback.customer_info?.name,
      feedback.data_origin,
      feedback.date,
      feedback.demographic,
      feedback.direction,
      feedback.ending_sentiment,
      feedback.engagement,
      feedback.hashtags,
      feedback.impression,
      feedback.labels,
      feedback.language,
      feedback.manager,
      feedback.mentions,
      feedback.nps_score,
      feedback.priority,
      feedback.products,
      feedback.rating_score,
      feedback.remarks,
      feedback.sentiment,
      feedback.source,
      feedback?.source_status,
      feedback.status,
      feedback.survey?.id,
      feedback.topic2_list,
      isStatic,
      languages,
      setShowFull,
      showFull,
    ],
  );

  return getFieldComponent;
};
